import _ from 'lodash';
import Notification from './notification';
import { formatter, unformatter } from '../utils/currencyFormatter';
import { doc, onSnapshot } from 'firebase/firestore';
import dogNames from 'dog-names';
import axios from 'axios';
import { produce } from 'immer';
import { Clout } from '../types/cloud';
import { getEarlyBirdCoupon } from './stripe';

const emptyCart = {
    tags: [],
    total: 0.0,
    fees: 0.0,
};

type Discount = {
    id: string,
    dollar_off?: number,
    percent_off?: number,
    name: string,
    hint: string
}

export const sumBy = (arr, key) => {
    return arr
        ?.flatMap((e) => unformatter(e[key]))
        ?.reduce((a, b) => a + b, 0);
};

export const canAndeePurchaseTags = () => {
    return axios.get('/marketplace/tags/purchase')
        .then(r => JSON.parse(r.data));
};


export const captureBetaEmail = async (email) => {
    const payload = { email };

    return axios.post('/marketplace/newsletter', payload)
        .then(r => r.data);
};
export const logout = async () => {
    return await axios.post('/auth/signout').then(() => {
        localStorage.removeItem('cart');
    });
};

export const login = async (payload) => {
    const { data } = await axios.post('/auth/signin', payload);

    if (!data.verified) {
        throw new Error('Authentication failed');
    }

    return data.andTagMain ?? data.andHandle;
};

export const getAuthToken = () => localStorage.getItem('token');
export const fetchAndeeData = async (tag) => {
    if (!tag) {
        throw new Error('No &tag found');
    }

    return await axios.get(`/marketplace/profile/${tag.trim()}`, { withCredentials: true })
        .then(({ data: { data: andee } }) => {
            return andee;
        })
        .catch((e) => {
            console.error(e);
            Notification.error('Error logging in');
        });
};

export const getMyTags = async () => {
    // TODO: Decode andee firestore id from JWT
    return axios.get('/marketplace/tags')
        .then(r => r.data);
};
export const isTagPopular = async (tag): Promise<Clout> => {
    const timeoutInMs = 15000; // 15 seconds
    return axios.get(`/popular/?handle=${tag}`, { timeout: timeoutInMs })
        .then(r => r.data)
        .catch(e => {
            if (e.code === 'ECONNABORTED') {
                return { popularity: { isPopular: true } };
            }
        });
};
export const getProfile = async (id) => {
    const cleanId = id.replace('&', '');
    return axios.get(`/marketplace/profile/${cleanId}`)
        .then(r => r.data);
};
export const createTempProfile = async (data) => {
    return axios.post('/profiles/new/temp', data)
        .then(r => r.data);
};
export const getTempProfile = async (tag) => {
    return axios.get(`/profiles/${tag.replace('&', '')}`)
        .then(r => r.data);
};
export const updateProfile = async (data) => {
    return axios.post('/marketplace/profile', data)
        .then(r => r.data);
};
export const searchTag = async (tag: string) => {
    const cleanTag = tag.replace('&', '');
    const response = await axios.get(`/marketplace/tags/${cleanTag}`, { withCredentials: true });
    return Promise.resolve(response.data || null);
};
export const generatePurpose = async (tag) => {
    const cleanTag = tag.replace('&', '');
    return axios.get(`/ai/smart-description?tag=${cleanTag}`)
        .then(r => r.data);
};

export const getTagPrice = async (tag) => {
    const cleanTag = tag.replace('&', '');
    const maxRetries = 3;
    const backoffMultiplier = 2000; // 2 seconds

    let retries = 0;
    let delay = backoffMultiplier;

    while (retries < maxRetries) {
        try {
            const response = await axios.get(`/price/${cleanTag}`);
            return response.data;
        } catch (error) {
            if (retries === maxRetries - 1) {
                throw error;
            }
            // eslint-disable-next-line no-loop-func
            await new Promise(resolve => setTimeout(resolve, delay));
            delay *= 2;
            retries++;
        }
    }
};

export const getTag = async (tag) => {
    const cleanTag = tag.replace('&', '');
    return axios.get(`/marketplace/tags/${cleanTag}`)
        .then(r => r.data);
};

export const updateTagOwner = async (tags: string, owner) => {
    const tagsArray = tags.split(',');
    const cleanTags: Tag[] = tagsArray.map(t => t.replace('&', '')) as any;
    const { ownerName, ownerPhone, ownerEmail } = owner;
    const payload = {
        tags: cleanTags,
        ownerName,
        ownerPhone,
        ownerEmail
    };

    return axios.post('/marketplace/tag/update', payload)
        .then(r => r.data);
};

export const addToCart = async (cart, tag, andee) => {
    const checkHold = await axios.get(`/marketplace/tags/${tag.text}/hold`, { withCredentials: true });

    if (checkHold.data.isOnHold) {
        Notification.warning(`Someone may be purchasing &${tag.text}. Please try again later.`);
        return Promise.reject('This tag is currently on hold');
    } else {
        const payload = {
            tag: tag.text,
            holdFor: andee.personId || andee.andeeId,
        };
        return axios.post('/marketplace/tag/hold', payload)
            .then((response) => {
                const updatedCart = produce(cart, (draftCart) => {
                    const index = draftCart.tags.findIndex(
                        (item) => item.text.trim() === tag.text.trim()
                    );
                    const notInCart = index === -1;

                    if (notInCart) {
                        draftCart.tags.push(tag);
                    } else {
                        draftCart.tags[index] = tag;
                    }

                    // sum the price of all tags in the cart
                    draftCart.total = sumBy(draftCart.tags, 'price');
                    draftCart.fees = draftCart.total * 0.1;
                    draftCart.discounts = [
                        {
                            id: 'renewal_fee_waiver',
                            dollarOff: draftCart.total * 0.1,
                            name: 'Renewal Fee Waiver',
                            hint: 'Renewal fee waived for 1 year'
                        },
                        {
                            id: getEarlyBirdCoupon(),
                            percentOff: 90,
                            name: 'Early Bird Discount',
                            hint: ''
                        }
                    ];
                });

                return updatedCart;
            });
    }
};

export const getDiscountById = async (id) => {
    return axios.get(`/stripe/discounts/${id}`)
        .then(r => r.data);
};

type Tag = {
    tag: string,
    text: string,
    price: string,
    isPopular: boolean,
    isNotForSale: boolean,
    instantSellAtFMV: boolean,
    instantSellPrice: string,
    discountId: string,
    discount: Discount
}

// TODO: Define types for tags (Tag, TempTag)
// the difference between a marketplace tag and brand tag is that
// we get the complete tag data from the db
export const addOneOrMoreBrandsToCart = async (tags: Tag[] = []) => {
    let totalPrice = 0;
    let discount: Discount | undefined = undefined;
    const updatedTags: Tag[] = [];

    for (const tag of tags) {
        if (!discount && tag.discountId) {
            discount = await getDiscountById(tag.discountId);
        }

        if (tag.instantSellAtFMV) {
            const { prediction } = await getTagPrice(tag.text);
            const price = Math.abs(parseInt(prediction));
            const cleanTag = tag.text.replace('&', '');
            const updatedTag: Tag = {
                ...tag,
                text: cleanTag,
                price: formatter.format(price),
            };

            totalPrice += price;

            updatedTags.push(updatedTag);
        } else {
            totalPrice += parseInt(tag.instantSellPrice);
            const updatedTag: Tag = {
                ...tag,
                price: formatter.format(parseInt(tag.instantSellPrice)),
            };
            updatedTags.push(updatedTag);
        }
    }

    const discounts: any[] = [
        {
            id: 'renewal_fee_waiver',
            dollarOff: totalPrice * 0.1,
            name: 'Renewal Fee Waiver',
            hint: 'Renewal fee waived for 1 year'
        }
    ];

    if (discount) {
        discounts.push({
            id: discount?.id,
            percentOff: discount.percent_off,
            name: 'Early Bird Discount',
            hint: ''
        });
    }

    return {
        tags: [...updatedTags],
        total: totalPrice,
        fees: totalPrice * 0.1,
        discounts
    };
};

export const getRecentlyClaimedTags = async () => {
    return axios.get('/marketplace/tags/claimed', { withCredentials: true })
        .then(r => r.data);
};

export const deleteTag = async (tag) => {
    return axios.delete(`/marketplace/tags/${tag}`)
        .then(r => r.data);
};

export const removeFromCart = async (cart, tag) => {
    const { tags } = cart;
    axios.post('/marketplace/tag/hold/remove', {
    tag: tag.text
    });
    return new Promise((resolve, reject) => {
        try {
            const index = tags.findIndex(
                (item) => item.text.trim() === tag.text.trim()
            );

            if (tags[index]) {
                const updatedCart = produce(cart, (draftCart) => {
                    draftCart.tags.splice(index, 1);
                    draftCart.total = sumBy(draftCart.tags, 'price');
                    draftCart.fees = draftCart.total * 0.1;
                });
                resolve(updatedCart);
            } else {
                resolve(cart);
            }
        } catch (e) {
            console.error(e);
            reject(e);
        }
    });
};

export const resetCart = async (setCart) => {
    localStorage.removeItem('cart');
    sessionStorage.removeItem('cart');
    setCart(emptyCart);
};
export const getNonce = async () => {
    return axios.get('/auth/nonce')
        .then(r => r.data);
};

export const authorizeOnce = async (tags: string, accessCode: string) => {
    const tagsArray = tags.split(',').map((t) => t.replace('&', '').trim());

    if (!tagsArray.length) {
        Notification.error('No tags selected');
        return;
    }

    if (tagsArray.length > 25) {
        Notification.error('You can only purchase up to 25 tags at a time');
        return;
    }

    const payload = {
        tags: tagsArray,
        accessCode
    };

    return axios
        .post('/auth/authorize-once', payload)
        .then((r) => r.data);
};
export const purchaseTags = async (andee, tags) => {
    const payload = {
        tags,
        ...(andee && {
            ownerId: andee.personId,
            ownerEmail: andee.emailAddress,
            ownerName: `${andee.firstName} ${andee.lastName}`,
            ownerPhone: andee.phoneNumber
        }),
    };

    // payment stuff here later
    return axios.post('/marketplace/tags/purchase', payload)
        .then(r => r.data);
};
export const updateTagprice = async (tag) => {
    if (!tag.text || !tag.price) {
        Notification.error('Invalid tag data');
        return;
    }
    const { pk, sk, price } = tag;
    const payload = {
        pk,
        sk,
        price
    };
    return axios.post('/marketplace/tag/price', payload)
        .then(r => r.data);
};

const transformRecsToTags = async (recs) => {
    const tags: object[] = [];
    for (const rec of recs) {
        const t = rec.toLowerCase();
        const tag = await searchTag(t)
            .then((data) => handleSearchPromise(data, t));

        // Exclude tags that are not for sale 
        if (tag.isNotForSale) continue;
        tags.push(tag);
    }
    return tags;
};

export const handleSearchPromise = (data, tag) => {
    const cleanTag = tag.replace('&', '').trim();
    return {
        text: `${cleanTag}`,
        price: data?.currentPrice || null,
        isPopular: data?.clout?.isPopular || false,
        isNotForSale: data?.isNotForSale || false,
        isDirectMatch: false,
    };
};

export const generateRecommendations = async (first = dogNames.allRandom(), last = dogNames.allRandom()) => {

    const firstInitial = first[0];
    const lastInitial = last[0];
    const firstLast = `${first}${last} `;
    const firstLastInitial = `${first}${lastInitial} `;
    const firstInitialLast = `${firstInitial}${last} `;
    const firstInitialLastInitialNumber = `${firstInitial}${lastInitial}${Math.floor(Math.random() * 10)} `;

    const recs = [
        first,
        last,
        firstLast,
        firstLastInitial,
        firstInitialLast,
        firstInitialLastInitialNumber,
    ];

    return await transformRecsToTags(recs);
};

export const generateRecommendationsFromSearch = async (searchKey) => {
    const recs = [
        `iam${searchKey}`,
        `thereal${searchKey}`,
        `its${searchKey}`,
        `${searchKey}24`,
    ];

    return await transformRecsToTags(recs);
};

let unsubscribe;
let retryTimeout;
let retryCount = 0;
export const listenForAuthSession = (db, nonceHex, setUser, setLoading) => {
    clearTimeout(retryTimeout);
    retryCount += 1;
    unsubscribe = onSnapshot(doc(db, 'authSession', nonceHex), (doc) => {
        if (doc.exists()) {
            const data = doc.data();
            if (data && !data.verified && data.expireTime > Date.now()) {
                unsubscribe();
                setLoading(true);
                login(data)
                    .then((andTag) => fetchAndeeData(andTag))
                    .then(() => {
                        setUser(data);
                    })
                    .catch((e) => {
                        console.error(e);
                        Notification.error('Error logging in');
                    });
            }
        }
    }, (err) => {
        if (retryCount < 5) {
            retryTimeout = setTimeout(() => {
                listenForAuthSession(db, nonceHex, setUser, setLoading);
            }, 1000 * exponentialInterval(retryCount)); // exponential backoff
        } else {
            console.error('retry count exceeded', err);
        }
    });
};

export const getCheckoutInfo = (cart) => {
    const payload = {
        cart,
    };
    return axios.post('/stripe/checkout', payload)
        .then(r => r.data);
};

export const holdBrandTags = async (payload) => {
    return axios.post('/marketplace/tags/hold', payload)
        .then(r => r.data);
};

export const getHeldBrandTags = async () => {
    return axios.get('/marketplace/tags/hold', { withCredentials: true })
        .then(r => r.data);
};

export const sendEmailConfirmation = async (cart) => {
    const payload = {
        cart
    };
    return axios.post('/marketplace/send-email', payload)
        .then(r => r.data);
};

function exponentialInterval(retryCount) {
    return Math.pow(2, retryCount);
}
